import { CoveoProductContextModel } from "@DSG/Components/Coveo/CoveoContext/CoveoProductContext";
import colorPallete, { bodyFonts, headlineFonts } from "@DSG/MobiusOverrides/styles/variables";
import ProductPriceSavingsMessage from "@DSG/Overrides/Components/ProductPriceSavingsMessage";
import { getSettingsCollection } from "@DSG/Overrides/Store/Context/ContextSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { getUnitListPrice, getUnitNetPrice, getUnitRegularPriceWithVat } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    coveoProduct: CoveoProductContextModel;
    currencySymbol?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CoveoProductPriceStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    currentPage: getCurrentPage(state).name,
    currentPageType: getCurrentPage(state).type,
    roles: state.context.session.userRoles,
    catalogSettings: state.context.settings.settingsCollection.catalogSettingSettings,
    isCoveoUser: state.context.session?.userRoles?.includes("Coveo-Access"),
    settingsCollection: getSettingsCollection(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;


export interface CoveoProductPriceStyles {
    wrapper?: any;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    unitOfMeasure?: UnitOfMeasureStyles;
    priceWrapper?: InjectableCss;
    price?: CoveoPriceStyles;
    invalidPriceText?: TypographyPresentationProps;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: CoveoPriceStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
}

interface CoveoPriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasure?: UnitOfMeasureStyles;
    /**
     * @deprecated Use the `unitOfMeasure.unitOfMeasureText` property instead.
     */
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
}

interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}

export const coveoProductPriceStyles: CoveoProductPriceStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    priceLabelText: {
        variant: "legend",
    },
    priceWrapper: {
        css: css`
            ${wrapInContainerStyles}
        `,
    },
    price: {
        priceText: { 
            css: css`
                align-items: center;
                display: flex;
                color: ${colorPallete.grey800};
                .price {
                    font: ${headlineFonts.headline2SemiBoldSm.fontWeight} ${headlineFonts.headline2SemiBoldSm.fontSize} /
                        ${headlineFonts.headline2SemiBoldSm.lineHeight} ${headlineFonts.headline2SemiBoldSm.fontFamily};
                    &.priceNotAvailable {
                        font-size: ${bodyFonts.body1SemiBoldLg.fontSize};
                        line-height: ${bodyFonts.body1SemiBoldLg.lineHeight};
                    }
                }
                .uom {
                    font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                        ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .price {
                                font-size: ${headlineFonts.headline2SemiBoldLg.fontSize};
                                line-height: ${headlineFonts.headline2SemiBoldLg.lineHeight};
                            }
                            .uom {
                                font-size: ${bodyFonts.body2RegularLg.fontSize};
                                line-height: ${bodyFonts.body2RegularLg.lineHeight};
                            }
                        `,
                    ],
                    "min",
                )}
            `
        },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    invalidPriceText: {
        color: "danger",
    },
    secondaryPriceWrapper: {
        css: css`
            margin-top: 5px;
            ${wrapInContainerStyles}
        `,
    },
    secondaryPrice: {
        priceText: { 
            css: css`
                align-items: center;
                display: flex;
                color: ${colorPallete.grey800};
                .price {
                    font: ${headlineFonts.headline2SemiBoldSm.fontWeight} ${headlineFonts.headline2SemiBoldSm.fontSize} /
                        ${headlineFonts.headline2SemiBoldSm.lineHeight} ${headlineFonts.headline2SemiBoldSm.fontFamily};
                    &.priceNotAvailable {
                        font-size: ${bodyFonts.body1SemiBoldLg.fontSize};
                        line-height: ${bodyFonts.body1SemiBoldLg.lineHeight};
                    }
                }
                .uom {
                    font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                        ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .price {
                                font-size: ${headlineFonts.headline2SemiBoldLg.fontSize};
                                line-height: ${headlineFonts.headline2SemiBoldLg.lineHeight};
                            }
                            .uom {
                                font-size: ${bodyFonts.body2RegularLg.fontSize};
                                line-height: ${bodyFonts.body2RegularLg.lineHeight};
                            }
                        `,
                    ],
                    "min",
                )}
            `
        },
        errorText: { 
            weight: "bold",
            css: css`
                color: ${colorPallete.grey600};
                font: ${bodyFonts.body2RegularLg.fontWeight} ${bodyFonts.body2RegularLg.fontSize}/${bodyFonts.body2RegularLg.lineHeight}  ${bodyFonts.body2RegularLg.fontFamily};
            `
        },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    packWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    packLabelText: {
        weight: "bold",
    },
    savingsMessageText: {
        italic: true,
        size: "12px",
    }
};

const SectionWrapper = getStyledWrapper("section");

const CoveoProductPrice = ({
    coveoProduct: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = true,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    showInvalidPriceMessage = false,
    enableVat,
    vatPriceDisplay,
    extendedStyles,
    currentPage,
    currentPageType,
    roles,
    catalogSettings,
    settingsCollection,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(coveoProductPriceStyles, extendedStyles));
    const coveoproductContextModel = pricingData as CoveoProductContextModel;
    const cartLineModel = pricingData as unknown as CartLineModel;

    const quoteRequired = coveoproductContextModel.coveoProduct
        ? coveoproductContextModel.coveoProduct.quoteRequired
        : cartLineModel.quoteRequired;

        const qtyOrdered = coveoproductContextModel?.coveoProduct
        ? coveoproductContextModel.coveoProductInfo?.qtyOrdered
        : cartLineModel?.qtyOrdered;
    
    const pricing = coveoproductContextModel.coveoProduct.pricing;

    const allowZeroPricing = pricingData.coveoProduct.allowZeroPricing;

    const isInvalidPrice =
         !quoteRequired &&
        // !isPromotionItem &&
         !allowZeroPricing &&
        pricing?.unitNetPrice === 0 &&
        pricing?.unitRegularPrice === 0;

    const unitOfMeasureDisplay = pricingData.coveoProductInfo.unitOfMeasure;
    const unitOfMeasure = pricing?.additionalResults?.realTimeUnitOfMeasure || pricing?.unitOfMeasure || unitOfMeasureDisplay;
   // const unitOfMeasure = unitOfMeasureDisplay;
    const priceStyles = styles.price || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};

    if (!canSeePrices) {
        return null;
    }
    const displayVatForPrice = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";
    const showSecondaryPrice = enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat";
    const salePriceLabel = cartLineModel?.salePriceLabel;

    let hasPriceSavings = false;
    if (pricing) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);

        hasPriceSavings = unitNetPrice < unitListPrice;
    }

    return (
        <SectionWrapper {...styles.wrapper} className={`priceWrapper ${hasPriceSavings ? 'hasSavings' : 'noSavings'}`}>
            {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
            <StyledWrapper {...styles.priceWrapper} data-test-selector="priceIncludeVat">
                <Price
                    currencySymbol={currencySymbol}
                    pricing={pricing}
                    qtyOrdered={qtyOrdered}
                    unitOfMeasure={unitOfMeasure}
                    showUnitOfMeasure={showUnitOfMeasure}
                    enableVat={enableVat}
                    displayWithVat={displayVatForPrice}
                    styles={priceStyles}
                    salePriceLabel={salePriceLabel}
                />
                {isInvalidPrice && showInvalidPriceMessage && (
                    <Typography as="p" {...styles.invalidPriceText}>
                        {translate("Invalid Price")}
                    </Typography>
                )}
                {pricing && showSavings && (
                    <ProductPriceSavingsMessage
                        pricing={pricing}
                        qtyOrdered={qtyOrdered || 1}
                        showSavingsAmount={showSavingsAmount}
                        showSavingsPercent={showSavingsPercent}
                        currencySymbol={currencySymbol}
                        unitOfMeasure={unitOfMeasure}
                        {...styles.savingsMessageText}
                    />
                )}
            </StyledWrapper>
        </SectionWrapper>
    );
}
interface PriceProps {
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: CoveoPriceStyles;
    salePriceLabel?: string;
}

const Price = ({
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
    salePriceLabel,
}: PriceProps) => {
    const salePriceLabelPrefix = "";
    let priceNotAvailable = false;
    if (pricing) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);
        // if (unitNetPrice < unitListPrice) {
        //     salePriceLabelPrefix = `${salePriceLabel} `;
        // }
        if(pricing.unitListPriceDisplay.toLocaleLowerCase() === "price not available." )
        {
            priceNotAvailable =true;
        }
    }
   
    
    return (
        <>
            {pricing ? (
                <>
                    <Typography {...styles.priceText} data-test-selector="coveoProductPrice_unitNetPrice">
                        <Typography className={`price ${priceNotAvailable ? 'priceNotAvailable' : 'priceAvailable'}`}>
                            {enableVat && displayWithVat
                            ?  getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay  
                            : `${getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay  }`}                                   
                        </Typography>
                        {(unitOfMeasure || pricing?.additionalResults?.realTimeUnitOfMeasure) && (!priceNotAvailable) &&
                            <Typography className="uom">
                                /{pricing?.additionalResults?.realTimeUnitOfMeasure || unitOfMeasure}
                            </Typography>
                        }
                    </Typography>
                </>
            ) : (
                <Typography {...styles.realTimeText} data-test-selector="coveoProductPrice_unitNetPrice_CurrencySymbol">
                    {currencySymbol}
                </Typography>
            )}
            {/* <UnitOfMeasure
                unitOfMeasure={unitOfMeasure}
                showUnitOfMeasure={showUnitOfMeasure}
                styles={ undefined}
            /> */}
            {pricing && enableVat && (
                <Typography as="p" {...styles.vatLabelText}>
                    {displayWithVat ? `${translate("Inc. VAT")} (${pricing.vatRate}%)` : translate("Ex. VAT")}
                </Typography>
            )}
        </>
    );
};

interface UnitOfMeasureProps {
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles?: UnitOfMeasureStyles;
}

const UnitOfMeasure = ({ unitOfMeasure, showUnitOfMeasure = true, styles }: UnitOfMeasureProps) => {
    return unitOfMeasure && showUnitOfMeasure ? (
        <Typography {...styles?.unitOfMeasureText} data-test-selector="coveoProductPrice_unitOfMeasureLabel">
            &nbsp;/&nbsp;{unitOfMeasure}
        </Typography>
    ) : null;
};
export default connect(mapStateToProps)(CoveoProductPrice);